import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { observer } from 'mobx-react';
import { Button } from '@material-ui/core';
import { isPolicyBillingHoldActive } from '@ourbranch/policy-utils';
import { getTodayStr } from '@ourbranch/date-helpers';

import { Banner } from 'core/components/banner';
import { Label } from 'core/components/label';
import { useStore } from 'core/store';
import { useToast } from 'core/components/toast';
import useSession from 'core/hooks/use-session';

import useStyles from './billing-hold-banner.styles';

/**
 * Persistent banner shown at the top of a policy while a billing hold ("Place on Hold", #33568) is
 * active. Surfaces the hold reason and an auto-release countdown to any viewer, and — for staff with
 * the billing-hold authority — a one-click "Remove Hold" that clears the hold and restores normal
 * billing. No-op for Branch policies (they don't carry the enhanced hold fields in practice).
 */
const BillingHoldBanner = observer(({ policy }) => {
  const classes = useStyles();
  const toast = useToast();
  const { canManageBillingHolds } = useSession();
  const {
    account: {
      fetchFullAccountAndPolicy,
      id: accountId,
      policies: { policy: policyStore }
    }
  } = useStore();
  const [removing, setRemoving] = useState(false);

  // Only surface for a staff-placed "Place on Hold" — which always carries a reason. A legacy
  // paid-ahead billing hold (no reason, e.g. on Branch) is intentionally NOT bannered, so Branch
  // behavior is unchanged.
  if (!isPolicyBillingHoldActive(policy) || !policy.billingHoldReason) {
    return null;
  }

  // State-aware day count (both dates parsed as calendar dates from the policy's timezone) so the
  // countdown doesn't drift by a day near midnight in the viewer's browser tz.
  const daysUntilRelease = differenceInCalendarDays(
    parseISO(policy.billingHoldUntil),
    parseISO(getTodayStr(policy.state))
  );
  const countdown =
    daysUntilRelease > 0
      ? ` — auto-releases in ${daysUntilRelease} day${daysUntilRelease === 1 ? '' : 's'}`
      : ' — auto-releases today';

  const handleRemove = async () => {
    setRemoving(true);
    try {
      await policyStore.removeBillingHold();
    } catch {
      toast.notify({ type: 'error', message: 'Failed to remove billing hold. Please try again.' });
      setRemoving(false);
      return;
    }
    // Removal succeeded. Refresh so the store reflects the cleared hold and this banner unmounts —
    // confirm()/setPreview only update self.policy for farm policy types (mirrors policy.jsx). A
    // refresh-only failure must NOT be reported as a removal failure, so it's handled separately.
    toast.notify({ type: 'success', message: 'Billing hold removed.' });
    try {
      await fetchFullAccountAndPolicy(policy.id, accountId);
    } catch {
      setRemoving(false); // refresh failed; the hold is removed but stays visible until reload
    }
  };

  return (
    <Banner color="orange" show iconType="alert">
      <div className={classes.content}>
        <Label type="smallWhite" className={classes.text}>
          <strong>Billing hold:</strong> {policy.billingHoldReason}
          {countdown}
          {policy.billingHoldBlocksCancellations ? ' · cancellations held' : ''}
        </Label>
        {canManageBillingHolds && (
          <Button
            variant="contained"
            color="secondary"
            size="small"
            className={classes.removeButton}
            onClick={handleRemove}
            disabled={removing}
          >
            {removing ? 'Removing…' : 'Remove Hold'}
          </Button>
        )}
      </div>
    </Banner>
  );
});

BillingHoldBanner.propTypes = {
  policy: PropTypes.shape({
    billingHoldUntil: PropTypes.string,
    billingHoldReason: PropTypes.string,
    billingHoldBlocksCancellations: PropTypes.bool,
    state: PropTypes.string
  })
};

BillingHoldBanner.defaultProps = {
  policy: undefined
};

export default BillingHoldBanner;
